import * as Sentry from '@sentry/browser';
import { hasPrivacyConsent } from './privacy-compliance.js';

type BreadcrumbLevel = 'debug' | 'info' | 'warn' | 'error'

let isInitialized = false;

/** @param {ErrorContext} [context] */
function normalizeContext(context: Record<string, unknown> = {}) {
  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => {
      if (value === null || value === undefined) {
        return [key, value];
      }

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return [key, value];
      }

      return [key, JSON.stringify(value)];
    })
  );
}

/** @param {BreadcrumbLevel} level */
function toSentryLevel(level: BreadcrumbLevel) {
  if (level === 'warn') {
    return 'warning';
  }

  return level;
}

export function initializeErrorTracking(): boolean {
  if (!hasPrivacyConsent('errorTracking')) {
    return false;
  }

  const typedWindow =
    typeof window !== 'undefined' ? /** @type {ErrorTrackingWindow} */ (window) : undefined;

  const dsn =
    import.meta.env.VITE_SENTRY_DSN ||
    typedWindow?.__AGENCY_SENTRY_DSN__;

  if (!dsn) {
    return false;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_APP_VERSION || 'development',
    maxBreadcrumbs: 100,
    sendDefaultPii: false,
    beforeSend(event) {
      return {
        ...event,
        tags: {
          ...event.tags,
          surface: 'web'
        }
      };
    }
  });

  isInitialized = true;
  return true;
}

/** @param {Record<string, unknown> | null | undefined} user */
export function setErrorTrackingUser(user: Record<string, unknown> | null | undefined) {
  if (!isInitialized) {
    return;
  }

  Sentry.setUser(user ?? null);
}

/** @param {BreadcrumbLevel} level @param {string} message @param {ErrorContext} [data] */
export function addErrorBreadcrumb(level: BreadcrumbLevel, message: string, data: Record<string, unknown> = {}) {
  if (!isInitialized) {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'app.log',
    level: toSentryLevel(level),
    message,
    data: normalizeContext(data)
  });
}

/** @param {unknown} error @param {ErrorContext} [context] @param {ErrorContext} [traceContext] */
export function captureTrackedError(error: unknown, context: Record<string, unknown> = {}, traceContext?: Record<string, unknown>) {
  const normalizedError = error instanceof Error ? error : new Error(String(error));

  if (typeof window !== 'undefined') {
    const typedWindow = /** @type {ErrorTrackingWindow} */ (window);

    if (!Array.isArray(typedWindow.__AGENCY_ERROR_EVENTS__)) {
      typedWindow.__AGENCY_ERROR_EVENTS__ = [];
    }

    typedWindow.__AGENCY_ERROR_EVENTS__.push({
      message: normalizedError.message,
      name: normalizedError.name,
      stack: normalizedError.stack,
      context: normalizeContext(context),
      trace: traceContext ? normalizeContext(traceContext) : undefined,
      timestamp: new Date().toISOString()
    });
  }

  if (!isInitialized || !hasPrivacyConsent('errorTracking')) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setContext('log_context', normalizeContext(context));

    if (traceContext) {
      scope.setContext('trace', normalizeContext(traceContext));
    }

    Sentry.captureException(normalizedError);
  });
}
